/**
 * <NameOrPickField> — a "pick an EXISTING one OR type a NEW name" combobox.
 *
 * The resolve-or-create primitive for case creation (operator 2026-07-06): the
 * user either SELECTS an existing entity (we keep its id → the backend uses it
 * verbatim) or TYPES a free-text name (→ the backend folds + reuses-or-creates).
 * A bare `<datalist>` can't return the chosen row's id, so this is a real
 * controlled combobox that distinguishes the two:
 *   - value = { mode: "existing", id, label }  → an existing row was clicked
 *   - value = { mode: "new", name }            → genuine free text
 *   - value = null                             → empty
 *
 * When the typed name folds (NFD+lowercase, ≡ lexserver `normalizeForMatch`) to
 * an existing row, we show an advisory "will reuse X" pre-warn — the server fold
 * stays authoritative, this is only a nudge so the lawyer doesn't create a dup.
 */

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { normalizeDisplayName } from "@/lib/workspace/display-name";

export interface PickOption {
  id: string;
  label: string;
  /** Optional secondary line (e.g. a client's @alias, a matter's type). */
  sublabel?: string;
}

/**
 * ⚖ **D-98 — `typed` vs `new` is a SECURITY BOUNDARY, not a cosmetic split.**
 *
 * `new` means the lawyer performed an EXPLICIT create act on a name that was
 * RENDERED ON SCREEN — clicking `＋ Criar «…»`, clicking a suggestion row, or
 * pressing Enter on text exactly matching a visible suggestion. Only `new`
 * authorises `createParents:true`, which mints a PERSISTENT org-wide client or
 * esfera that a typo would make permanent.
 *
 * `typed` is free text still in flight. It fails CLOSED: the request omits
 * `createParents`, the gateway refuses with its near-miss "did you mean …",
 * and the lawyer is guided to the ＋ Criar row.
 *
 * ⚠ **Do NOT re-merge these two.** Before D-98 they were one `mode:"new"`
 * emitted BOTH per-keystroke and on a real commit, so nothing downstream could
 * tell "the user typed Mari and stopped" from "the user chose to create Maria
 * Oliveira". Wiring `createParents` to that collapsed state would mint a client
 * named "Mari" — destroying the exact near-miss protection D-70(c) provides,
 * and destroying it in the reassuring direction, because the dialog SUCCEEDS.
 *
 * Ruled 2026-08-10 (⚖ C-1) on `case-folder-shape-pin-and-reset-plan.md` D-98;
 * as-built in `ai-docs/lexide-d98-create-parents-miniplan.md`.
 */
export type NameOrPickValue =
  | { mode: "existing"; id: string; label: string }
  | { mode: "typed"; name: string }
  | { mode: "new"; name: string }
  | null;

/**
 * THE security predicate — an explicit create act (⚖ D-98). Gate
 * `createParents:true` on this and NOTHING else.
 */
export function isExplicitCreate(v: NameOrPickValue): v is { mode: "new"; name: string } {
  return v?.mode === "new";
}

/**
 * The AFFORDANCE predicate — a non-existing name is present, committed or not.
 *
 * Use for deciding which INPUTS to show (the pessoa-física toggle, the esfera
 * type select) and for per-keystroke derivations. Deliberately true for `typed`
 * so the form does not re-layout at the moment of commit, and so keystroke-
 * driven helpers keep working. **Never gate a persistent write on this** — that
 * is `isExplicitCreate`.
 */
export function hasNewName(v: NameOrPickValue): v is { mode: "typed" | "new"; name: string } {
  return v?.mode === "typed" || v?.mode === "new";
}

export interface NameOrPickFieldProps {
  options: PickOption[];
  value: NameOrPickValue;
  onChange: (v: NameOrPickValue) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Label used in the "＋ Create «…»" row, e.g. "cliente" / "matéria". */
  createNoun: string;
  /** i18n-provided string for the create row: `Criar «{{name}}»`. */
  createLabel: (name: string) => string;
  /** i18n-provided string for the reuse pre-warn: `Reutilizará: {{name}}`. */
  reuseLabel: (name: string) => string;
  inputClassName?: string;
  /** Cap on TYPED input (default 200). Constrains typing only — an existing
   *  pick with a longer legacy label still displays fine (React sets the
   *  value programmatically; `maxLength` limits keystrokes, not state). */
  maxLength?: number;
  /** Optional canonical NEW-name suggestions (e.g. the matter vocabulary),
   *  rendered in their own section below the existing options. Picking one
   *  commits it as a NEW name (`mode:"new"`) — the backend still folds/dedups.
   *  Filtered by the query like the options. */
  suggestions?: SuggestionRow[];
  /** Section header shown above the suggestions (when any are visible). */
  suggestionsLabel?: string;
  "data-testid"?: string;
}

export interface SuggestionRow {
  /** The canonical name committed on pick (mode:"new"). */
  name: string;
  /** Small secondary line (e.g. the matter-type label). */
  sublabel?: string;
}

/** Display text for a value (existing label / new name / empty). */
function displayOf(value: NameOrPickValue): string {
  if (!value) return "";
  return value.mode === "existing" ? value.label : value.name;
}

export function NameOrPickField({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  createNoun,
  createLabel,
  reuseLabel,
  inputClassName,
  maxLength = 200,
  suggestions = [],
  suggestionsLabel,
  "data-testid": testId,
}: NameOrPickFieldProps) {
  // `query` non-null = actively editing; null = show the committed value's display.
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const display = query !== null ? query : displayOf(value);
  const q = (query ?? "").trim();
  const qFold = normalizeDisplayName(q);

  const filtered =
    qFold.length === 0
      ? options
      : options.filter((o) => normalizeDisplayName(o.label).includes(qFold));
  const exactExisting = q ? options.find((o) => normalizeDisplayName(o.label) === qFold) : undefined;

  // Suggestion rows (e.g. matter vocabulary): filtered by the query, and never
  // duplicating a shown existing option's fold.
  const optionFolds = new Set(options.map((o) => normalizeDisplayName(o.label)));
  const filteredSuggestions = suggestions.filter((s) => {
    const f = normalizeDisplayName(s.name);
    if (optionFolds.has(f)) return false;
    return qFold.length === 0 ? true : f.includes(qFold);
  });
  // When the typed text folds to a suggestion exactly, that suggestion row IS the
  // canonical create — suppress the generic "Criar «typed»" row to avoid a dup.
  const exactSuggestion = q ? filteredSuggestions.find((s) => normalizeDisplayName(s.name) === qFold) : undefined;

  // Advisory reuse pre-warn: a genuine free-text name that folds to an existing row.
  const reuseWarn =
    value?.mode === "new" && exactExisting && exactExisting.label !== value.name
      ? reuseLabel(exactExisting.label)
      : null;

  // Close the dropdown on an outside click (the dialog itself doesn't close).
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function pick(o: PickOption) {
    onChange({ mode: "existing", id: o.id, label: o.label });
    setQuery(null);
    setOpen(false);
  }

  function commitNew(name: string) {
    const n = name.trim();
    onChange(n ? { mode: "new", name: n } : null);
    setQuery(null);
    setOpen(false);
  }

  return (
    <div ref={rootRef} className="relative">
      <input
        data-testid={testId}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        value={display}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={maxLength}
        onFocus={() => {
          setQuery(displayOf(value));
          setOpen(true);
        }}
        onChange={(e) => {
          const v = e.target.value;
          setQuery(v);
          setOpen(true);
          // ⚖ D-98: typing emits `typed`, NEVER `new`. This handler fires on
          // EVERY KEYSTROKE, so a `new` here would make "Mari" mid-word
          // indistinguishable from a deliberate create — see the type's docblock.
          onChange(v.trim() ? { mode: "typed", name: v.trim() } : null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            if (open) {
              // Nested Escape: close the dropdown first. Stop the native event so
              // a parent modal's window-level Escape handler does NOT also fire
              // and discard the whole form (a second Escape, dropdown closed,
              // propagates normally to close the dialog).
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              setOpen(false);
              setQuery(null);
            }
          } else if (e.key === "Enter" && open) {
            e.preventDefault();
            // ⚠ AN EXACT EXISTING ROW WINS, and it is checked FIRST.
            // Previously the single-option branch required
            // `filteredSuggestions.length === 0`, so typing a real client's
            // exact name while any "create new" suggestion was also listed
            // fell through to `commitNew(q)` — committing free text that
            // merely LOOKS like the record, which the backend then refuses
            // with "no client named …". The field displayed the right name
            // while submitting a different value, so the user saw an error
            // naming a client they believed they had chosen (2026-08-10).
            // Picking beats typing; an exact name is a pick.
            if (exactExisting) pick(exactExisting);
            else if (filtered.length === 1 && filteredSuggestions.length === 0) pick(filtered[0]);
            else if (exactSuggestion) commitNew(exactSuggestion.name);
            // ⚖ D-98 / C-1 — bare free text is NOT a commit, and this arm is
            // deliberately a NO-OP.
            //
            // It used to `commitNew(q)`. The ruling: Enter commits only a name
            // the lawyer SAW rendered (an exact suggestion, above) — because
            // this listbox has no active-item highlight, so Enter on bare text
            // chooses nothing that is displayed as chosen, while a lifetime of
            // forms trains Enter as submit/next.
            //
            // The value is already `typed` from the keystroke handler, so doing
            // nothing is exactly right — and we deliberately DO NOT close the
            // dropdown, because the `＋ Criar «…»` row is the affordance the
            // lawyer now needs. Enter is preventDefault'ed above, so this can
            // never submit the form.
            //
            // ⚠ Do not "tidy" this into a commit or an `onChange`: writing
            // `typed` here would also DOWNGRADE an already-committed `new` when
            // the user focuses a committed field and presses Enter without
            // editing.
          }
        }}
        className={inputClassName}
      />

      {open && !disabled && (filtered.length > 0 || filteredSuggestions.length > 0 || q) && (
        <ul
          data-testid={testId ? `${testId}-listbox` : undefined}
          role="listbox"
          className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-cream-200/70 bg-white py-1 shadow-lg dark:border-coffee-700/60 dark:bg-coffee-900"
        >
          {filtered.map((o) => (
            <li key={o.id} role="option" aria-selected={value?.mode === "existing" && value.id === o.id}>
              <button
                type="button"
                data-testid={testId ? `${testId}-option` : undefined}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(o);
                }}
                className="flex w-full flex-col items-start px-3 py-1.5 text-left text-body-sm text-coffee-800 hover:bg-cream-100/70 dark:text-cream-200 dark:hover:bg-coffee-800/60"
              >
                <span className="truncate">{o.label}</span>
                {o.sublabel && <span className="text-caption text-cream-500">{o.sublabel}</span>}
              </button>
            </li>
          ))}
          {filteredSuggestions.length > 0 && (
            <>
              {suggestionsLabel && (
                <li
                  aria-hidden="true"
                  className="border-t border-cream-200/60 px-3 pb-0.5 pt-1.5 text-caption font-medium uppercase tracking-wide text-cream-500 dark:border-coffee-700/50"
                >
                  {suggestionsLabel}
                </li>
              )}
              {filteredSuggestions.map((s) => (
                <li key={`sug:${s.name}`} role="option">
                  <button
                    type="button"
                    data-testid={testId ? `${testId}-suggestion` : undefined}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      commitNew(s.name);
                    }}
                    className="flex w-full flex-col items-start px-3 py-1.5 text-left text-body-sm text-coffee-800 hover:bg-cream-100/70 dark:text-cream-200 dark:hover:bg-coffee-800/60"
                  >
                    <span className="truncate">{s.name}</span>
                    {s.sublabel && <span className="text-caption text-cream-500">{s.sublabel}</span>}
                  </button>
                </li>
              ))}
            </>
          )}
          {q && !exactExisting && !exactSuggestion && (
            <li role="option">
              <button
                type="button"
                data-testid={testId ? `${testId}-create` : undefined}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commitNew(q);
                }}
                className="flex w-full items-center gap-1.5 border-t border-cream-200/60 px-3 py-1.5 text-left text-body-sm font-medium text-gold-600 hover:bg-cream-100/70 dark:border-coffee-700/50 dark:text-gold-500 dark:hover:bg-coffee-800/60"
              >
                {createLabel(q)}
              </button>
            </li>
          )}
        </ul>
      )}

      {reuseWarn && (
        <p
          data-testid={testId ? `${testId}-reuse-warn` : undefined}
          className="mt-1 text-caption text-coffee-500 dark:text-cream-500"
        >
          {reuseWarn}
        </p>
      )}
      {/* Tiny cue that a brand-new entity will be created. */}
      {value?.mode === "new" && !reuseWarn && (
        <p className="mt-1 text-caption text-gold-600 dark:text-gold-500">
          {createLabel(value.name)} · {createNoun}
        </p>
      )}
    </div>
  );
}
